<template>
  <div class="overview-page">
    <!-- 頁面標題 -->
    <h1 class="page-title">角色權限列表</h1>

    <v-container>
      <!-- 篩選條 -->
      <v-card class="filter-card">
        <v-row class="filter-row" align="center">
          <v-col cols="12" sm="6" md="3">
            <v-select v-model="filters.roleName" :items="roleNames" label="角色名稱" outlined dense hide-details
              class="custom-field"></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <VueDatePicker v-model="filters.creationDateRange" range locale="zh" :format="formatRange"
              placeholder="建立日期範圍" type="date" :enable-time="false" :show-action-buttons="false" class="custom-field"
              teleport="body" />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="filters.creatorAccount" label="建立者帳號" outlined dense hide-details
              class="custom-field"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <VueDatePicker v-model="filters.modificationDateRange" range locale="zh" :format="formatRange"
              placeholder="修改日期範圍" type="date" :enable-time="false" :show-action-buttons="false" class="custom-field"
              teleport="body" />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="filters.modifierAccount" label="修改者帳號" outlined dense hide-details
              class="custom-field"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select v-model="filters.status" :items="statuses" label="狀態" outlined dense hide-details
              class="custom-field"></v-select>
          </v-col>
        </v-row>

        <!-- 查詢與清除按鈕 -->
        <v-row class="filter-actions" align="center" justify="end">
          <v-btn outlined color="primary" @click="applyFilters">查詢</v-btn>
          <v-btn outlined color="secondary" @click="clearFilters" class="ml-2">清除</v-btn>
        </v-row>
      </v-card>

      <!-- 資料表 -->
      <v-card class="table-card">
        <!-- 新增角色按鈕 -->
        <div class="table-header-row">
          <v-btn color="primary" outlined class="ml-2" @click="openAddRoleDialog">
            <v-icon left>mdi-plus</v-icon> 新增角色
          </v-btn>

        </div>
        <table class="custom-table">
          <thead>
            <tr>
              <th v-for="header in headers" :key="header.value" class="table-header sortable-header"
                @click="header.value !== 'action' && sortTable(header.value)">
                {{ header.text }}
                <v-icon small v-if="header.value !== 'action'">{{ getSortIcon(header.value) }}</v-icon>
              </th>
              <th class="table-header actions-column">操作</th>
            </tr>
          </thead>

          <!-- 表格內容 -->
          <tbody>
            <tr v-for="role in filteredRoles" :key="role.name">
              <td class="table-cell">{{ role.name }}</td>
              <td class="table-cell">{{ role.creationDate }}</td>
              <td class="table-cell">{{ role.creatorAccount }}</td>
              <td class="table-cell">{{ role.modificationDate }}</td>
              <td class="table-cell">{{ role.modifierAccount }}</td>
              <td class="table-cell">{{ role.status }}</td>
              <td class="table-cell actions-cell">
                <v-icon small class="action-icon" @click="viewRole(role)">mdi-eye</v-icon>
                <v-icon small class="action-icon" @click="editRole(role)">mdi-pencil</v-icon>
              </td>
            </tr>
          </tbody>
        </table>
      </v-card>
    </v-container>
    <v-dialog v-model="addRoleDialog" max-width="500px" class="custom-dialog">
      <v-card>
        <v-card-title class="dialog-title">
          <v-icon small class="title-icon">mdi-plus</v-icon> 新增角色
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-content">
          <v-row>
            <v-col cols="4" class="field-label">角色名稱</v-col>
            <v-col cols="8">
              <v-text-field v-model="newRole.name" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="field-label">建立者帳號</v-col>
            <v-col cols="8">
              <v-text-field v-model="newRole.creatorAccount" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="field-label">狀態</v-col>
            <v-col cols="8">
              <v-select v-model="newRole.status" :items="statuses" outlined dense></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn text color="primary" @click="saveNewRole">保存</v-btn>
          <v-btn text color="secondary" @click="closeAddRoleDialog">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 詳細資料對話框 -->
    <v-dialog v-model="detailDialog" max-width="500px" class="custom-dialog">
      <v-card>
        <v-card-title class="dialog-title">
          <v-icon small class="title-icon">mdi-eye</v-icon> 角色詳細資料
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-content">
          <p><strong>角色名稱:</strong> {{ selectedRole.name }}</p>
          <p><strong>建立日期:</strong> {{ selectedRole.creationDate }}</p>
          <p><strong>建立者帳號:</strong> {{ selectedRole.creatorAccount }}</p>
          <p><strong>修改日期:</strong> {{ selectedRole.modificationDate }}</p>
          <p><strong>修改者帳號:</strong> {{ selectedRole.modifierAccount }}</p>
          <p><strong>狀態:</strong> {{ selectedRole.status }}</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn text color="primary" @click="closeDetailDialog">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- 編輯角色對話框 -->
    <v-dialog v-model="editDialog" max-width="500px" class="custom-dialog">
      <v-card>
        <v-card-title class="dialog-title">
          <v-icon small class="title-icon">mdi-pencil</v-icon> 編輯角色資料
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-content">
          <v-row>
            <v-col cols="4" class="field-label">角色名稱</v-col>
            <v-col cols="8">
              <v-text-field v-model="selectedRole.name" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="field-label">建立者帳號</v-col>
            <v-col cols="8">
              <v-text-field v-model="selectedRole.creatorAccount" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="field-label">修改者帳號</v-col>
            <v-col cols="8">
              <v-text-field v-model="selectedRole.modifierAccount" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4" class="field-label">狀態</v-col>
            <v-col cols="8">
              <v-select v-model="selectedRole.status" :items="statuses" outlined dense></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn text color="primary" @click="saveRole">保存</v-btn>
          <v-btn text color="secondary" @click="closeEditDialog">取消</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>



  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  name: "RolesView",
  components: { VueDatePicker },
  data() {
    return {
      headers: [
        { text: "角色名稱", value: "name" },
        { text: "建立日期", value: "creationDate" },
        { text: "建立者帳號", value: "creatorAccount" },
        { text: "修改日期", value: "modificationDate" },
        { text: "修改者帳號", value: "modifierAccount" },
        { text: "狀態", value: "status" },
      ],
      roles: [
        {
          name: "管理員",
          creationDate: "2024-12-01",
          creatorAccount: "admin1",
          modificationDate: "2024-12-10",
          modifierAccount: "admin2",
          status: "啟用",
        },
        {
          name: "操作員",
          creationDate: "2024-12-05",
          creatorAccount: "admin3",
          modificationDate: "2024-12-15",
          modifierAccount: "admin4",
          status: "禁用",
        },
        {
          name: "訪客",
          creationDate: "2024-12-10",
          creatorAccount: "admin5",
          modificationDate: "2024-12-20",
          modifierAccount: "admin6",
          status: "啟用",
        },
      ],
      filters: {
        roleName: "",
        creatorAccount: "",
        modifierAccount: "",
        status: "",
        creationDateRange: [],
        modificationDateRange: [],
      },
      roleNames: ["管理員", "操作員", "訪客"],
      statuses: ["啟用", "禁用"],
      detailDialog: false,
      selectedRole: {},
      editDialog: false, // 控制編輯對話框的顯示
      addRoleDialog: false,
    newRole: {
      name: "",
      creatorAccount: "",
      status: "",
    },
    };
  },
  computed: {
    filteredRoles() {
      return this.roles.filter((role) => {
        const matchesName = !this.filters.roleName || role.name === this.filters.roleName;
        const matchesCreator = !this.filters.creatorAccount || role.creatorAccount.includes(this.filters.creatorAccount);
        const matchesModifier = !this.filters.modifierAccount || role.modifierAccount.includes(this.filters.modifierAccount);
        const matchesStatus = !this.filters.status || role.status === this.filters.status;

        const matchesCreationDate =
          this.filters.creationDateRange.length === 2
            ? new Date(role.creationDate).getTime() >=
            new Date(this.filters.creationDateRange[0]).setHours(0, 0, 0, 0) &&
            new Date(role.creationDate).getTime() <=
            new Date(this.filters.creationDateRange[1]).setHours(23, 59, 59, 999)
            : true;

        const matchesModificationDate =
          this.filters.modificationDateRange.length === 2
            ? new Date(role.modificationDate).getTime() >=
            new Date(this.filters.modificationDateRange[0]).setHours(0, 0, 0, 0) &&
            new Date(role.modificationDate).getTime() <=
            new Date(this.filters.modificationDateRange[1]).setHours(23, 59, 59, 999)
            : true;

        return matchesName && matchesCreator && matchesModifier && matchesStatus && matchesCreationDate && matchesModificationDate;
      });
    },
  },
  methods: {
    openAddRoleDialog() {
    this.newRole = { name: "", creatorAccount: "", status: "" }; // 清空表單
    this.addRoleDialog = true;
  },
  closeAddRoleDialog() {
    this.addRoleDialog = false;
  },
  saveNewRole() {
    if (this.newRole.name && this.newRole.creatorAccount && this.newRole.status) {
      // 保存新角色
      this.roles.push({
        ...this.newRole,
        creationDate: new Date().toISOString().split("T")[0], // 自動生成日期
        modificationDate: new Date().toISOString().split("T")[0],
        modifierAccount: this.newRole.creatorAccount,
      });
      this.addRoleDialog = false;
      console.log("新增角色成功:", this.newRole);
    } else {
      alert("請填寫所有必填字段！");
    }
  },
    sortTable(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }

      this.roles.sort((a, b) => {
        const valueA = a[key];
        const valueB = b[key];

        if (this.sortOrder === "asc") {
          return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        } else {
          return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
        }
      });
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return "mdi-swap-vertical";
      return this.sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    formatRange(range) {
      if (!range || range.length !== 2) return "";
      const [start, end] = range;
      return `${start.toISOString().split("T")[0]} ~ ${end.toISOString().split("T")[0]}`;
    },
    clearFilters() {
      this.filters = {
        roleName: "",
        creatorAccount: "",
        modifierAccount: "",
        status: "",
        creationDateRange: [],
        modificationDateRange: [],
      };
    },
    applyFilters() {
      console.log("Filters applied:", this.filters);
    },
    addRole() {
      console.log("新增角色");
    },
    viewRole(role) {
      this.selectedRole = role;
      this.detailDialog = true;
    },
    closeDetailDialog() {
      this.detailDialog = false;
    },
    editRole(role) {
      this.selectedRole = { ...role }; // 深拷貝角色數據
      this.editDialog = true; // 打開編輯對話框
    },
    saveRole() {
      const index = this.roles.findIndex(
        (role) => role.name === this.selectedRole.name
      );
      if (index !== -1) {
        this.roles.splice(index, 1, { ...this.selectedRole }); // 更新角色數據
      }
      this.editDialog = false; // 關閉對話框
      console.log("保存成功:", this.selectedRole);
    },
    closeEditDialog() {
      this.editDialog = false; // 關閉對話框
    },
  },
};
</script>

<style scoped>
.table-header {
  background-color: #333333;
  color: white;
  font-weight: bold;
  padding: 10px;
  cursor: pointer;
}

.sort-icon {
  margin-left: 8px;
  font-size: 14px;
  color: #ffffff;
}

.table-cell {
  text-align: center;
  /* 水平置中 */
  vertical-align: middle;
  /* 垂直置中 */
  padding: 8px;
  border-bottom: 1px solid #dcdcdc;
  color: #333333;
}

/* 背景與標題 */
.overview-page {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
}

/* 篩選條樣式 */
.filter-card {
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #dcdcdc;
}

.filter-row {
  margin-bottom: 16px;
}

.filter-actions {
  margin-top: 10px;
}

/* 表格樣式 */
.table-card {
  border-radius: 8px;
  overflow: hidden;
  padding: 16px;
  border: 1px solid #dcdcdc;
  background-color: #ffffff;
}

.table-header-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.custom-table {
  width: 100%;
  /* 確保表格寬度填滿容器 */
  table-layout: fixed;
  /* 修正表格布局 */
  border-collapse: collapse;
  /* 合併表格邊框 */
}

.custom-table th,
.custom-table td {
  text-align: center;
  /* 單元格內容居中對齊 */
  vertical-align: middle;
  /*
  padding: 10px; /* 單元格內邊距，增加間距 */
}

.custom-table th {
  background-color: #333;
  color: white;
  font-weight: bold;
}

.custom-table td {
  border-bottom: 1px solid #ddd;
}

.actions-cell {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-icon {
  cursor: pointer;
  color: #666;
}

.action-icon:hover {
  color: #333;
}

.custom-dialog {
  --v-overlay-scrim-background: rgba(0, 0, 0, 0.4);
}

.dialog-title {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  color: #333;
  padding: 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #dcdcdc;
  display: flex;
  align-items: center;
  gap: 8px;
}


.dialog-content {
  background-color: #ffffff;
  /* 白底 */
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
}

.field-label {
  font-weight: bold;
  color: #333;
  display: flex;
  align-items: center;
}


.dialog-content p {
  margin: 8px 0;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  background-color: #f9f9f9;
}

.dialog-actions .v-btn {
  font-size: 14px;
  padding: 8px 16px;
  margin-left: 8px;
  border-radius: 8px;
}

.dialog-actions .v-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
</style>
